import React, { useState } from 'react';
import { getFontOptions } from '../config/fontOptions.js';

export const ColumnTypographyControls = ({
    values = {},
    onChange, // (field, value) => void
    customFonts = [],
    showHeading = true,
    showText = true,
    headingTitle = '📝 Column Heading Typography',
    textTitle = '📄 Column Body Text Typography'
}) => {
    const [typoMode, setTypoMode] = useState('desktop'); // 'desktop' | 'mobile'
    const fonts = getFontOptions(customFonts, true, '', 'Default / Inherit');

    const handlePropChange = (field, val) => {
        if (!onChange) return;
        onChange(field, val);
        // Bidirectional normalization
        if (field === 'headingWeight') onChange('headingFontWeight', val);
        if (field === 'headingFontWeight') onChange('headingWeight', val);
        if (field === 'headingSize') onChange('headingFontSize', val);
        if (field === 'headingFontSize') onChange('headingSize', val);
        if (field === 'headingTransform') onChange('headingTextTransform', val);
        if (field === 'headingTextTransform') onChange('headingTransform', val);

        if (field === 'textWeight') onChange('textFontWeight', val);
        if (field === 'textFontWeight') onChange('textWeight', val);
        if (field === 'textSize') onChange('textFontSize', val);
        if (field === 'textFontSize') onChange('textSize', val);
        if (field === 'textTransform') onChange('textTextTransform', val);
        if (field === 'textTextTransform') onChange('textTransform', val);

        if (field === 'mobileTextFontSize') onChange('mobileBodyFontSize', val);
        if (field === 'mobileBodyFontSize') onChange('mobileTextFontSize', val);
    };

    const settingLabelStyle = {
        fontSize: '7.5px',
        color: '#64748b',
        display: 'block',
        marginBottom: '2px',
        fontWeight: '600'
    };

    const inputStyle = {
        padding: '3px 5px',
        border: '1px solid #cbd5e1',
        borderRadius: '3px',
        fontSize: '9px',
        width: '100%',
        background: '#fff',
        color: '#334155',
        boxSizing: 'border-box',
        outline: 'none'
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Viewport Mode Switcher */}
            <div style={{ display: 'flex', gap: '4px', background: '#e2e8f0', padding: '2px', borderRadius: '4px' }}>
                <button
                    type="button"
                    onClick={() => setTypoMode('desktop')}
                    style={{
                        flex: 1,
                        padding: '4px 6px',
                        fontSize: '9px',
                        fontWeight: 'bold',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        background: typoMode === 'desktop' ? '#3b82f6' : 'transparent',
                        color: typoMode === 'desktop' ? '#ffffff' : '#64748b',
                        transition: 'all 0.15s ease'
                    }}
                >
                    🖥️ Desktop Typography
                </button>
                <button
                    type="button"
                    onClick={() => setTypoMode('mobile')}
                    style={{
                        flex: 1,
                        padding: '4px 6px',
                        fontSize: '9px',
                        fontWeight: 'bold',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        background: typoMode === 'mobile' ? '#3b82f6' : 'transparent',
                        color: typoMode === 'mobile' ? '#ffffff' : '#64748b',
                        transition: 'all 0.15s ease'
                    }}
                >
                    📱 Mobile Overrides (≤768px)
                </button>
            </div>

            {/* DESKTOP VIEW */}
            {typoMode === 'desktop' && (
                <>
                    {/* HEADING TYPOGRAPHY */}
                    {showHeading && (
                        <div style={{ background: '#f8fafc', padding: '6px 8px', borderRadius: '4px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span>{headingTitle}</span>
                            </div>

                            {/* Font Family */}
                            <div>
                                <label style={settingLabelStyle}>Font Family</label>
                                <select
                                    value={values.headingFontFamily || ''}
                                    onChange={e => handlePropChange('headingFontFamily', e.target.value)}
                                    style={inputStyle}
                                >
                                    {fonts.map((f, idx) => (
                                        <option key={idx} value={f.value}>{f.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Font Size & Font Weight */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                                <div>
                                    <label style={settingLabelStyle}>Font Size</label>
                                    <input
                                        type="text"
                                        value={values.headingSize || values.headingFontSize || ''}
                                        placeholder="e.g. 1.25rem or 20px"
                                        onChange={e => handlePropChange('headingSize', e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>
                                <div>
                                    <label style={settingLabelStyle}>Font Weight</label>
                                    <select
                                        value={values.headingWeight || values.headingFontWeight || ''}
                                        onChange={e => handlePropChange('headingWeight', e.target.value)}
                                        style={inputStyle}
                                    >
                                        <option value="">Default (Inherit)</option>
                                        <option value="100">100 - Thin</option>
                                        <option value="200">200 - Extra Light</option>
                                        <option value="300">300 - Light</option>
                                        <option value="400">400 - Normal</option>
                                        <option value="500">500 - Medium</option>
                                        <option value="600">600 - Semi-Bold</option>
                                        <option value="700">700 - Bold</option>
                                        <option value="800">800 - Extra-Bold</option>
                                        <option value="900">900 - Black</option>
                                    </select>
                                </div>
                            </div>

                            {/* Line Height & Letter Spacing */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                                <div>
                                    <label style={settingLabelStyle}>Line Height</label>
                                    <input
                                        type="text"
                                        value={values.headingLineHeight || ''}
                                        placeholder="e.g. 1.2, 1.4, 28px"
                                        onChange={e => handlePropChange('headingLineHeight', e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>
                                <div>
                                    <label style={settingLabelStyle}>Letter Spacing</label>
                                    <input
                                        type="text"
                                        value={values.headingLetterSpacing || ''}
                                        placeholder="e.g. 0.05em or 1px"
                                        onChange={e => handlePropChange('headingLetterSpacing', e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>
                            </div>

                            {/* Alignment & Text Transform */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                                <div>
                                    <label style={settingLabelStyle}>Alignment</label>
                                    <select
                                        value={values.headingAlign || ''}
                                        onChange={e => handlePropChange('headingAlign', e.target.value)}
                                        style={inputStyle}
                                    >
                                        <option value="">Default (Left)</option>
                                        <option value="left">Left</option>
                                        <option value="center">Center</option>
                                        <option value="right">Right</option>
                                        <option value="justify">Justify</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={settingLabelStyle}>Text Transform</label>
                                    <select
                                        value={values.headingTransform || values.headingTextTransform || 'none'}
                                        onChange={e => handlePropChange('headingTransform', e.target.value)}
                                        style={inputStyle}
                                    >
                                        <option value="none">Normal</option>
                                        <option value="uppercase">UPPERCASE</option>
                                        <option value="lowercase">lowercase</option>
                                        <option value="capitalize">Capitalize</option>
                                    </select>
                                </div>
                            </div>

                            {/* Heading Color */}
                            <div>
                                <label style={settingLabelStyle}>Heading Color</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <input
                                        type="color"
                                        value={(values.headingColor && values.headingColor.startsWith('#')) ? values.headingColor : '#1e293b'}
                                        onChange={e => handlePropChange('headingColor', e.target.value)}
                                        style={{ width: '20px', height: '20px', padding: 0, border: 'none', borderRadius: '3px', cursor: 'pointer', flexShrink: 0 }}
                                    />
                                    <input
                                        type="text"
                                        value={values.headingColor || ''}
                                        placeholder="Inherit / #1e293b"
                                        onChange={e => handlePropChange('headingColor', e.target.value)}
                                        style={{ ...inputStyle, flex: 1 }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* BODY TEXT TYPOGRAPHY */}
                    {showText && (
                        <div style={{ background: '#f8fafc', padding: '6px 8px', borderRadius: '4px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span>{textTitle}</span>
                            </div>

                            {/* Font Family */}
                            <div>
                                <label style={settingLabelStyle}>Font Family</label>
                                <select
                                    value={values.textFontFamily || ''}
                                    onChange={e => handlePropChange('textFontFamily', e.target.value)}
                                    style={inputStyle}
                                >
                                    {fonts.map((f, idx) => (
                                        <option key={idx} value={f.value}>{f.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Font Size & Font Weight */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                                <div>
                                    <label style={settingLabelStyle}>Font Size</label>
                                    <input
                                        type="text"
                                        value={values.textSize || values.textFontSize || ''}
                                        placeholder="e.g. 1rem or 16px"
                                        onChange={e => handlePropChange('textSize', e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>
                                <div>
                                    <label style={settingLabelStyle}>Font Weight</label>
                                    <select
                                        value={values.textWeight || values.textFontWeight || ''}
                                        onChange={e => handlePropChange('textWeight', e.target.value)}
                                        style={inputStyle}
                                    >
                                        <option value="">Default (Inherit)</option>
                                        <option value="100">100 - Thin</option>
                                        <option value="200">200 - Extra Light</option>
                                        <option value="300">300 - Light</option>
                                        <option value="400">400 - Normal</option>
                                        <option value="500">500 - Medium</option>
                                        <option value="600">600 - Semi-Bold</option>
                                        <option value="700">700 - Bold</option>
                                        <option value="800">800 - Extra-Bold</option>
                                        <option value="900">900 - Black</option>
                                    </select>
                                </div>
                            </div>

                            {/* Line Height & Letter Spacing */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                                <div>
                                    <label style={settingLabelStyle}>Line Height</label>
                                    <input
                                        type="text"
                                        value={values.textLineHeight || ''}
                                        placeholder="e.g. 1.5, 1.6, 24px"
                                        onChange={e => handlePropChange('textLineHeight', e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>
                                <div>
                                    <label style={settingLabelStyle}>Letter Spacing</label>
                                    <input
                                        type="text"
                                        value={values.textLetterSpacing || ''}
                                        placeholder="e.g. normal or 0.5px"
                                        onChange={e => handlePropChange('textLetterSpacing', e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>
                            </div>

                            {/* Alignment & Text Transform */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                                <div>
                                    <label style={settingLabelStyle}>Alignment</label>
                                    <select
                                        value={values.textAlign || ''}
                                        onChange={e => handlePropChange('textAlign', e.target.value)}
                                        style={inputStyle}
                                    >
                                        <option value="">Default (Left)</option>
                                        <option value="left">Left</option>
                                        <option value="center">Center</option>
                                        <option value="right">Right</option>
                                        <option value="justify">Justify</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={settingLabelStyle}>Text Transform</label>
                                    <select
                                        value={values.textTransform || values.textTextTransform || 'none'}
                                        onChange={e => handlePropChange('textTransform', e.target.value)}
                                        style={inputStyle}
                                    >
                                        <option value="none">Normal</option>
                                        <option value="uppercase">UPPERCASE</option>
                                        <option value="lowercase">lowercase</option>
                                        <option value="capitalize">Capitalize</option>
                                    </select>
                                </div>
                            </div>

                            {/* Body Text Color */}
                            <div>
                                <label style={settingLabelStyle}>Body Text Color</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <input
                                        type="color"
                                        value={(values.textColor && values.textColor.startsWith('#')) ? values.textColor : '#475569'}
                                        onChange={e => handlePropChange('textColor', e.target.value)}
                                        style={{ width: '20px', height: '20px', padding: 0, border: 'none', borderRadius: '3px', cursor: 'pointer', flexShrink: 0 }}
                                    />
                                    <input
                                        type="text"
                                        value={values.textColor || ''}
                                        placeholder="Inherit / #475569"
                                        onChange={e => handlePropChange('textColor', e.target.value)}
                                        style={{ ...inputStyle, flex: 1 }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* MOBILE OVERRIDES VIEW */}
            {typoMode === 'mobile' && (
                <>
                    <div style={{ background: '#eff6ff', padding: '6px 8px', borderRadius: '4px', border: '1px solid #bfdbfe', fontSize: '9px', color: '#1e40af' }}>
                        💡 Styles set here take effect exclusively on mobile viewports (≤ 768px). Leave blank to inherit desktop typography.
                    </div>

                    {/* MOBILE HEADING TYPOGRAPHY */}
                    {showHeading && (
                        <div style={{ background: '#f8fafc', padding: '6px 8px', borderRadius: '4px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span>📱 Mobile Heading Overrides</span>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                                <div>
                                    <label style={settingLabelStyle}>Mobile Heading Font Size</label>
                                    <input
                                        type="text"
                                        value={values.mobileHeadingFontSize || ''}
                                        placeholder="e.g. 1.1rem or 18px"
                                        onChange={e => handlePropChange('mobileHeadingFontSize', e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>
                                <div>
                                    <label style={settingLabelStyle}>Mobile Heading Alignment</label>
                                    <select
                                        value={values.mobileHeadingAlign || 'inherit'}
                                        onChange={e => handlePropChange('mobileHeadingAlign', e.target.value)}
                                        style={inputStyle}
                                    >
                                        <option value="inherit">Inherit Desktop / Global</option>
                                        <option value="left">Left</option>
                                        <option value="center">Center</option>
                                        <option value="right">Right</option>
                                        <option value="justify">Justify</option>
                                    </select>
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                                <div>
                                    <label style={settingLabelStyle}>Mobile Line Height</label>
                                    <input
                                        type="text"
                                        value={values.mobileHeadingLineHeight || ''}
                                        placeholder="e.g. 1.2 or 1.3"
                                        onChange={e => handlePropChange('mobileHeadingLineHeight', e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>
                                <div>
                                    <label style={settingLabelStyle}>Mobile Letter Spacing</label>
                                    <input
                                        type="text"
                                        value={values.mobileHeadingLetterSpacing || ''}
                                        placeholder="e.g. 0.02em"
                                        onChange={e => handlePropChange('mobileHeadingLetterSpacing', e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>
                            </div>

                            <div>
                                <label style={settingLabelStyle}>Mobile Heading Bottom Margin</label>
                                <input
                                    type="text"
                                    value={values.mobileHeadingMarginBottom || ''}
                                    placeholder="e.g. 8px or 12px"
                                    onChange={e => handlePropChange('mobileHeadingMarginBottom', e.target.value)}
                                    style={inputStyle}
                                />
                            </div>
                        </div>
                    )}

                    {/* MOBILE BODY TEXT TYPOGRAPHY */}
                    {showText && (
                        <div style={{ background: '#f8fafc', padding: '6px 8px', borderRadius: '4px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span>📱 Mobile Body Text Overrides</span>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                                <div>
                                    <label style={settingLabelStyle}>Mobile Body Font Size</label>
                                    <input
                                        type="text"
                                        value={values.mobileTextFontSize || values.mobileBodyFontSize || ''}
                                        placeholder="e.g. 0.85rem or 14px"
                                        onChange={e => handlePropChange('mobileTextFontSize', e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>
                                <div>
                                    <label style={settingLabelStyle}>Mobile Text Alignment</label>
                                    <select
                                        value={values.mobileTextAlign || 'inherit'}
                                        onChange={e => handlePropChange('mobileTextAlign', e.target.value)}
                                        style={inputStyle}
                                    >
                                        <option value="inherit">Inherit Desktop / Global</option>
                                        <option value="left">Left</option>
                                        <option value="center">Center</option>
                                        <option value="right">Right</option>
                                        <option value="justify">Justify</option>
                                    </select>
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                                <div>
                                    <label style={settingLabelStyle}>Mobile Line Height</label>
                                    <input
                                        type="text"
                                        value={values.mobileTextLineHeight || ''}
                                        placeholder="e.g. 1.4 or 1.5"
                                        onChange={e => handlePropChange('mobileTextLineHeight', e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>
                                <div>
                                    <label style={settingLabelStyle}>Mobile Letter Spacing</label>
                                    <input
                                        type="text"
                                        value={values.mobileTextLetterSpacing || ''}
                                        placeholder="e.g. normal or 0.2px"
                                        onChange={e => handlePropChange('mobileTextLetterSpacing', e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>
                            </div>

                            {/* Quick Align Bar */}
                            <div>
                                <label style={settingLabelStyle}>Quick Column Align Sync</label>
                                <div style={{ display: 'flex', gap: '3px' }}>
                                    {[
                                        { id: 'left', label: '⬅ Left' },
                                        { id: 'center', label: '↔ Center' },
                                        { id: 'right', label: '➡ Right' },
                                        { id: 'justify', label: '☰ Justify' }
                                    ].map(a => (
                                        <button
                                            key={a.id}
                                            type="button"
                                            onClick={() => {
                                                handlePropChange('mobileTextAlign', a.id);
                                                handlePropChange('mobileHeadingAlign', a.id);
                                            }}
                                            style={{
                                                flex: 1,
                                                padding: '3px 4px',
                                                fontSize: '8px',
                                                border: '1px solid #cbd5e1',
                                                borderRadius: '3px',
                                                background: values.mobileTextAlign === a.id ? '#3b82f6' : '#ffffff',
                                                color: values.mobileTextAlign === a.id ? '#ffffff' : '#334155',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {a.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ColumnTypographyControls;

